import { Editor, Element, Node, Point, Range, Text, Transforms } from "slate";
import { VariableDefinition } from "@common/helpers/variable-field";
import { escapeRegExp } from "@helpers/escapeRegExp";
import { VariableElement } from "./variableTextTypes";

export const VARIABLE_TRIGGER = "#";

/** Registers the variable as a void inline so Slate treats it as one atom (single-backspace delete). */
export function withVariables(editor: Editor): Editor {
    const { isInline, isVoid } = editor;

    editor.isInline = (element) =>
        element.type === "variable" ? true : isInline(element);

    editor.isVoid = (element) =>
        element.type === "variable" ? true : isVoid(element);

    return editor;
}

function isVariableNode(node: Node): boolean {
    return Element.isElement(node) && node.type === "variable";
}

function pointHasVariable(editor: Editor, point: Point): boolean {
    return Boolean(
        Editor.above(editor, {
            at: point,
            match: (node) => isVariableNode(node),
        }),
    );
}

function contentBeforeNeedsSpace(editor: Editor, point: Point): boolean {
    const [node] = Editor.node(editor, point);
    if (Text.isText(node) && point.offset > 0) {
        return !/\s/.test(node.text[point.offset - 1]);
    }

    let current: Point | undefined = point;
    for (let step = 0; step < 20; step++) {
        current = Editor.before(editor, current);
        if (!current) {
            return false;
        }
        if (pointHasVariable(editor, current)) {
            return true;
        }
        const [neighbor] = Editor.node(editor, current);
        if (Text.isText(neighbor) && neighbor.text.length > 0) {
            const character = neighbor.text[current.offset];
            if (character === undefined) {
                continue;
            }
            return !/\s/.test(character);
        }
    }
    return false;
}

function contentAfterNeedsSpace(editor: Editor, point: Point): boolean {
    const [node] = Editor.node(editor, point);
    if (Text.isText(node) && point.offset < node.text.length) {
        return !/\s/.test(node.text[point.offset]);
    }

    let current: Point | undefined = point;
    for (let step = 0; step < 20; step++) {
        current = Editor.after(editor, current);
        if (!current) {
            return false;
        }
        if (pointHasVariable(editor, current)) {
            return true;
        }
        const [neighbor] = Editor.node(editor, current);
        if (Text.isText(neighbor) && neighbor.text.length > 0) {
            const character = neighbor.text[current.offset];
            if (character === undefined) {
                continue;
            }
            return !/\s/.test(character);
        }
    }
    return false;
}

export function insertVariable(editor: Editor, variableKey: string): void {
    const { selection } = editor;
    const shouldInsertSpaceBefore = selection
        ? contentBeforeNeedsSpace(editor, Range.start(selection))
        : false;
    const shouldInsertSpaceAfter = selection
        ? contentAfterNeedsSpace(editor, Range.end(selection))
        : false;

    if (selection && !Range.isCollapsed(selection)) {
        Transforms.delete(editor);
    }
    if (shouldInsertSpaceBefore) {
        Transforms.insertText(editor, " ");
    }

    const variable: VariableElement = {
        type: "variable",
        variableKey,
        children: [{ text: "" }],
    };
    Transforms.insertNodes(editor, variable);
    Transforms.move(editor);

    if (shouldInsertSpaceAfter) {
        Transforms.insertText(editor, " ");
    }
}

interface VariableTrigger {
    target: Range;
    search: string;
}

export function getVariableTrigger(
    editor: Editor,
    trigger: string = VARIABLE_TRIGGER,
): VariableTrigger | null {
    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) {
        return null;
    }

    const [start] = Range.edges(selection);

    const after = Editor.after(editor, start);
    let hasWordBoundaryAfter = true;
    if (after) {
        const afterRange = Editor.range(editor, start, after);
        hasWordBoundaryAfter = /^(\s|$)/.test(
            Editor.string(editor, afterRange),
        );
    }
    if (!hasWordBoundaryAfter) {
        return null;
    }

    const charBefore = Editor.before(editor, start, { unit: "character" });
    const charBeforeRange =
        charBefore && Editor.range(editor, charBefore, start);
    const charBeforeText =
        charBeforeRange && Editor.string(editor, charBeforeRange);
    if (charBeforeRange && charBeforeText === trigger) {
        return { target: charBeforeRange, search: "" };
    }

    const wordBefore = Editor.before(editor, start, { unit: "word" });
    const before = wordBefore && Editor.before(editor, wordBefore);
    const beforeRange = before && Editor.range(editor, before, start);
    const beforeText = beforeRange && Editor.string(editor, beforeRange);
    const match =
        beforeText &&
        new RegExp(`^${escapeRegExp(trigger)}(\\w*)$`).exec(beforeText);

    if (match && beforeRange) {
        return { target: beforeRange, search: match[1] };
    }

    return null;
}

/** Filters by search over key + label. */
export function filterVariables(
    variables: VariableDefinition[],
    search: string,
): VariableDefinition[] {
    const query = search.trim().toLowerCase();
    if (!query) {
        return variables;
    }
    return variables.filter(
        (variable) =>
            variable.label.toLowerCase().includes(query) ||
            variable.key.toLowerCase().includes(query),
    );
}
